import React from "react";

import AuditActionLink from "../../audit/AuditActionLink";
import PurchaseOrderStatusBadge from "./PurchaseOrderStatusBadge";
import type { PurchaseOrderStatus } from "../../../purchaseOrders";
import Button from "../../ui/Button";

export interface PurchaseOrderActionPermissions {
  canApprove: boolean;
  canReceive: boolean;
  canClose: boolean;
  canReopen: boolean;
}

export interface PurchaseOrderHeaderActionsProps extends PurchaseOrderActionPermissions {
  purchaseOrderId: number | null;
  poStatus: PurchaseOrderStatus | string;
  poNumber: string | null;
  busy: boolean;
  onApprove: () => void;
  onClose: () => void;
  onReopen: () => void;
}

export function PurchaseOrderHeaderActions({
  purchaseOrderId,
  poStatus,
  poNumber,
  busy,
  canApprove,
  canReceive,
  canClose,
  canReopen,
  onApprove,
  onClose,
  onReopen,
}: PurchaseOrderHeaderActionsProps) {
  if (!purchaseOrderId) {
    return <PurchaseOrderStatusBadge status="draft" />;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <PurchaseOrderStatusBadge status={poStatus} />
      <AuditActionLink
        slug="purchase-order"
        id={purchaseOrderId}
        name={poNumber}
      />
      {canApprove && (
        <Button
          type="button"
          disabled={busy}
          loading={busy}
          variant="primary"
          size="md"
          onClick={onApprove}
        >
          Approve
        </Button>
      )}
      {canReceive && (
        <Button
          type="button"
          variant="success"
          size="md"
          onClick={() =>
            window.open(
              `/purchase-orders/${purchaseOrderId}/receive`,
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          Receive delivery
        </Button>
      )}
      {canClose && (
        <Button
          type="button"
          disabled={busy}
          variant="secondary"
          size="md"
          onClick={onClose}
        >
          Close PO
        </Button>
      )}
      {canReopen && (
        <Button
          type="button"
          disabled={busy}
          variant="secondary"
          size="md"
          onClick={onReopen}
        >
          Reopen
        </Button>
      )}
    </div>
  );
}

export interface PurchaseOrderFooterActionsProps extends PurchaseOrderActionPermissions {
  isNew: boolean;
  busy: boolean;
  loading: boolean;
  saving: boolean;
  actionLoading: boolean;
  saveLabel: string;
  onSave: () => void;
  onCreateAndApprove: () => void;
  onApprove: () => void;
}

export function PurchaseOrderFooterActions({
  isNew,
  busy,
  loading,
  saving,
  actionLoading,
  saveLabel,
  canApprove,
  onSave,
  onCreateAndApprove,
  onApprove,
}: PurchaseOrderFooterActionsProps) {
  const disabled = busy || loading;

  return (
    <>
      <Button
        type="button"
        disabled={disabled}
        loading={saving}
        variant="secondary"
        size="md"
        onClick={onSave}
      >
        {saveLabel}
      </Button>
      {isNew ? (
        <Button
          type="button"
          disabled={disabled}
          loading={saving}
          variant="primary"
          size="md"
          onClick={onCreateAndApprove}
        >
          Create & approve
        </Button>
      ) : (
        canApprove && (
          <Button
            type="button"
            disabled={disabled}
            loading={actionLoading}
            variant="primary"
            size="md"
            onClick={onApprove}
          >
            Approve
          </Button>
        )
      )}
    </>
  );
}
